import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { usePlayer } from '../context/PlayerContext';
import { getThemeColors } from '../utils/themeStyles';
import {
  getSceneData,
  CHARACTER_IMAGES,
  CHARACTER_COLORS,
  CHARACTER_SIDES,
  SceneBeat,
} from '../data/mockSceneData';
import { getScenarioForMission, getRuntimeCharacter } from '../data/scenarios';
import { STORY_CHARACTERS } from '../data/storyCharacters';

// In-memory seen scenes tracker (resets on app reload)
const seenScenes = new Set<string>();
const PLAYER_AVATAR_MAP: Record<string, any> = {
  egocrisis: require('../../assets/images/characters/egocrisis_raoul.jpg'),
  foundation_gamble: require('../../assets/images/characters/foundation_gamble_lucas_man_30s.jpg'),
  architecture_under_fire: require('../../assets/images/characters/egocrisis_raoul.jpg'),
  data_storm: require('../../assets/images/characters/data_storm_aisha_woman_30s.jpg'),
  cloud_heist: require('../../assets/images/characters/cloud_heist_keiko_woman_30s.jpg'),
  neural_override: require('../../assets/images/characters/neural_override_karim_man_40s.jpg'),
  pipeline_inferno: require('../../assets/images/characters/pipeline_inferno_viktor_man_30s.jpg'),
  merge_conflict: require('../../assets/images/characters/merge_conflict_priya_woman_30s.jpg'),
  deadline_paradox: require('../../assets/images/characters/deadline_paradox_sakura_woman_30s.jpg'),
  quantum_gambit: require('../../assets/images/characters/data_storm_jun_woman_30s.jpg'),
  arabic_modern: require('../../assets/images/characters/arabic_modern_natasha_woman_40s.jpg'),
  learning_machine: require('../../assets/images/characters/data_storm_jun_woman_30s.jpg'),
  ink_protocol: require('../../assets/images/characters/merge_conflict_viktor_man_30s.jpg'),
};
// ─── Player override for dynamic Raoul avatar/name ────────────────────────────
let _playerName = 'Joueur';
let _playerAvatar: any = null;
let _currentStoryKey = 'egocrisis';
let _missionId = 0;

function resolveStoryCharacterAvatar(characterName: string): any {
  const chars = STORY_CHARACTERS[_currentStoryKey] ?? [];
  const lower = characterName.toLowerCase();
  const exact = chars.find(c => c.name.toLowerCase() === lower);
  if (exact?.avatar) return exact.avatar;
  const firstName = lower.split(' ')[0];
  const byFirstName = chars.find(c => c.name.toLowerCase().startsWith(firstName));
  return byFirstName?.avatar;
}

// Visuel d'un personnage (image + couleur). Priorité au scénario BLOB
// (couleur + avatar URL blob) ; repli sur les avatars/couleurs bundlés locaux.
function resolveCharacterVisual(rawName: string): { img: any; color: string } {
  if (rawName === 'Raoul') {
    return {
      img: _playerAvatar || PLAYER_AVATAR_MAP[_currentStoryKey] || PLAYER_AVATAR_MAP.egocrisis,
      color: CHARACTER_COLORS[rawName] ?? '#6366F1',
    };
  }
  const rc = getRuntimeCharacter(_missionId, rawName);
  if (rc) {
    const img = rc.avatarUrl
      ? { uri: rc.avatarUrl }
      : (resolveStoryCharacterAvatar(rawName) || CHARACTER_IMAGES[rawName]);
    return { img, color: rc.color || CHARACTER_COLORS[rawName] || '#6366F1' };
  }
  return {
    img: resolveStoryCharacterAvatar(rawName) || CHARACTER_IMAGES[rawName],
    color: CHARACTER_COLORS[rawName] ?? '#6366F1',
  };
}

function toScenarioBeats(narrativeText: string): SceneBeat[] {
  const lines = narrativeText
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);
  const beats: SceneBeat[] = [];

  const pushAction = (text: string) => {
    const cleaned = text.trim().replace(/^\*|\*$/g, '').trim();
    if (cleaned.length > 0) beats.push({ type: 'action', text: cleaned });
  };

  for (const line of lines) {
    if (line.startsWith('**') && line.includes(':**')) {
      const marker = line.indexOf(':**');
      const name = line.slice(2, marker).trim();
      let remainder = line.slice(marker + 3).trim();

      // Split cinematic/action fragment from spoken line when format is:
      // **Name :** *Action* "Dialogue"
      const actionMatch = remainder.match(/^\*(.+?)\*\s*(.*)$/);
      if (actionMatch) {
        pushAction(`${name} : ${actionMatch[1]}`);
        remainder = actionMatch[2].trim();
      }

      let spoken = remainder;
      const quotedMatch = remainder.match(/"([^"]+)"/);
      if (quotedMatch) {
        spoken = quotedMatch[1].trim();
      } else {
        spoken = spoken.replace(/^"|"$/g, '').trim();
      }

      if (spoken.length > 0) {
        beats.push({
          type: 'dialogue',
          character: name,
          side: CHARACTER_SIDES[name] ?? 'left',
          text: spoken,
        });
      }
      continue;
    }

    // Full italic line is treated as cinematic/action.
    if (line.startsWith('*') && line.endsWith('*')) {
      pushAction(line);
      continue;
    }

    const cleanedNarration = line.replace(/^\*|\*$/g, '').trim();
    if (cleanedNarration.length > 0) {
      beats.push({ type: 'narration', text: cleanedNarration });
    }
  }
  return beats;
}

function getScenarioSceneData(missionId?: number, chapterId?: number, sceneNumber?: number) {
  if (!missionId || !sceneNumber) return undefined;
  const scenario = getScenarioForMission(missionId);
  const chapter = scenario?.chapters?.[(chapterId ?? 1) - 1] ?? scenario?.chapters?.[0];
  if (!chapter) return undefined;
  const storyItems = chapter.sequence.filter(i => i.type === 'story');
  const sceneItem = storyItems[sceneNumber - 1];
  if (!sceneItem || typeof sceneItem.content !== 'object' || !('narrativeText' in sceneItem.content)) {
    return undefined;
  }
  return {
    id: sceneNumber,
    title: sceneItem.title,
    location: scenario?.title ?? 'Scenario',
    beats: toScenarioBeats(String(sceneItem.content.narrativeText)),
  };
}

// ─── Beat renderers ───────────────────────────────────────────────────────────

function TypewriterText({
  text,
  style,
  onComplete,
  skipRef,
}: {
  text: string;
  style: any;
  onComplete: () => void;
  skipRef: React.MutableRefObject<boolean>;
}) {
  const words = text.split(' ');
  const [visibleCount, setVisibleCount] = useState(skipRef.current ? words.length : 0);

  useEffect(() => {
    if (skipRef.current) {
      setVisibleCount(words.length);
      onComplete();
      return;
    }
    setVisibleCount(0);
    let idx = 0;
    const interval = setInterval(() => {
      if (skipRef.current) {
        clearInterval(interval);
        setVisibleCount(words.length);
        onComplete();
        return;
      }
      idx++;
      setVisibleCount(idx);
      if (idx >= words.length) {
        clearInterval(interval);
        onComplete();
      }
    }, 30);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const displayed = words.slice(0, visibleCount).join(' ');
  return <Text style={style}>{displayed}</Text>;
}

function NarrationBeatAnimated({ beat, dark, onComplete, skipRef }: { beat: SceneBeat; dark: boolean; onComplete: () => void; skipRef: React.MutableRefObject<boolean> }) {
  return (
    <View style={[nb.wrap, { borderLeftColor: dark ? '#4B5563' : '#D1D5DB', backgroundColor: dark ? '#1F2937' : '#F9FAFB' }]}>
      <TypewriterText
        text={beat.text}
        style={[nb.text, { color: dark ? '#D1D5DB' : '#374151' }]}
        onComplete={onComplete}
        skipRef={skipRef}
      />
    </View>
  );
}

function ActionBeatAnimated({ beat, dark, onComplete, skipRef }: { beat: SceneBeat; dark: boolean; onComplete: () => void; skipRef: React.MutableRefObject<boolean> }) {
  return (
    <View style={[ab.wrap]}>
      <TypewriterText
        text={`[ ${beat.text} ]`}
        style={[ab.text, { color: dark ? '#9CA3AF' : '#6B7280' }]}
        onComplete={onComplete}
        skipRef={skipRef}
      />
    </View>
  );
}

function DialogueBeatAnimated({ beat, dark, onComplete, skipRef }: { beat: SceneBeat; dark: boolean; onComplete: () => void; skipRef: React.MutableRefObject<boolean> }) {
  const rawName = beat.character ?? '';
  const charName = rawName === 'Raoul' ? _playerName : rawName;
  const side = beat.side ?? CHARACTER_SIDES[rawName] ?? 'left';
  const isLeft = side === 'left';
  const { img, color } = resolveCharacterVisual(rawName);
  const [imgError, setImgError] = useState(false);
  const avatarSize = 72;
  const bubbleBg = dark ? '#1E293B' : '#FFFFFF';
  const bubbleBorder = color + '55';

  return (
    <View style={[db.row, isLeft ? db.rowLeft : db.rowRight]}>
      <View style={[db.charCol, isLeft ? db.charColLeft : db.charColRight]}>
        <View style={[db.avatarWrap, { width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2, borderColor: color }]}>
          {img && !imgError ? (
            <Image source={img} onError={() => setImgError(true)} style={{ width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2 }} resizeMode="cover" />
          ) : (
            <View style={[db.avatarFallback, { backgroundColor: color, width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2 }]}>
              <Text style={db.avatarInitial}>{charName[0] ?? '?'}</Text>
            </View>
          )}
        </View>
        <Text style={[db.name, { color: color }]}>{charName}</Text>
        {beat.expression ? <Text style={[db.expression, { color: dark ? '#9CA3AF' : '#6B7280' }]}>({beat.expression})</Text> : null}
      </View>
      <View style={[db.bubble, { backgroundColor: bubbleBg, borderColor: bubbleBorder, borderTopLeftRadius: isLeft ? 4 : 16, borderTopRightRadius: isLeft ? 16 : 4 }, isLeft ? db.bubbleLeft : db.bubbleRight]}>
        <TypewriterText
          text={beat.text}
          style={[db.bubbleText, { color: dark ? '#E5E7EB' : '#1F2937' }]}
          onComplete={onComplete}
          skipRef={skipRef}
        />
      </View>
    </View>
  );
}

const nb = StyleSheet.create({
  wrap: { borderLeftWidth: 3, paddingLeft: 16, paddingVertical: 12, marginVertical: 8, borderRadius: 4 },
  text: { fontSize: 15, fontStyle: 'italic', lineHeight: 24 },
});

function ActionBeat({ beat, dark }: { beat: SceneBeat; dark: boolean }) {
  return (
    <View style={[ab.wrap]}>
      <Text style={[ab.text, { color: dark ? '#9CA3AF' : '#6B7280' }]}>[ {beat.text} ]</Text>
    </View>
  );
}
const ab = StyleSheet.create({
  wrap: { alignItems: 'center', marginVertical: 8 },
  text: { fontSize: 13, fontStyle: 'italic' },
});

function DialogueBeat({
  beat,
  dark,
  large,
}: {
  beat: SceneBeat;
  dark: boolean;
  large?: boolean;
}) {
  const rawName = beat.character ?? '';
  const charName = rawName === 'Raoul' ? _playerName : rawName;
  const side = beat.side ?? CHARACTER_SIDES[rawName] ?? 'left';
  const isLeft = side === 'left';
  const { img, color } = resolveCharacterVisual(rawName);
  const [imgError, setImgError] = useState(false);

  const avatarSize = large ? 72 : 44;
  const bubbleBg = dark ? '#1E293B' : '#FFFFFF';
  const bubbleBorder = color + '55';

  return (
    <View style={[db.row, isLeft ? db.rowLeft : db.rowRight]}>
      {/* Avatar column */}
      <View style={[db.charCol, isLeft ? db.charColLeft : db.charColRight]}>
        <View style={[db.avatarWrap, { width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2, borderColor: color }]}>
          {img && !imgError ? (
            <Image source={img} onError={() => setImgError(true)} style={{ width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2 }} resizeMode="cover" />
          ) : (
            <View style={[db.avatarFallback, { backgroundColor: color, width: avatarSize, height: avatarSize, borderRadius: avatarSize / 2 }]}>
              <Text style={db.avatarInitial}>{charName[0] ?? '?'}</Text>
            </View>
          )}
        </View>
        <Text style={[db.name, { color: color }]}>{charName}</Text>
        {beat.expression ? (
          <Text style={[db.expression, { color: dark ? '#9CA3AF' : '#6B7280' }]}>({beat.expression})</Text>
        ) : null}
      </View>

      {/* Bubble */}
      <View
        style={[
          db.bubble,
          {
            backgroundColor: bubbleBg,
            borderColor: bubbleBorder,
            borderTopLeftRadius: isLeft ? 4 : 16,
            borderTopRightRadius: isLeft ? 16 : 4,
          },
          isLeft ? db.bubbleLeft : db.bubbleRight,
        ]}
      >
        <Text style={[db.bubbleText, { color: dark ? '#E5E7EB' : '#1F2937' }]}>{beat.text}</Text>
      </View>
    </View>
  );
}
const db = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'flex-start', marginVertical: 10, gap: 10 },
  rowLeft: { justifyContent: 'flex-start' },
  rowRight: { justifyContent: 'flex-end' },
  charCol: { alignItems: 'center', width: 72, flexShrink: 0 },
  charColLeft: {},
  charColRight: {},
  avatarWrap: { borderWidth: 2.5, overflow: 'hidden' },
  avatarFallback: { justifyContent: 'center', alignItems: 'center' },
  avatarInitial: { color: '#FFF', fontSize: 20, fontWeight: '700' },
  name: { fontSize: 12, fontWeight: '700', marginTop: 4, textAlign: 'center' },
  expression: { fontSize: 10, fontStyle: 'italic', textAlign: 'center', marginTop: 2 },
  bubble: {
    flex: 1,
    borderWidth: 1.5,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    padding: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
    maxWidth: '80%',
  },
  bubbleLeft: {},
  bubbleRight: {},
  bubbleText: { fontSize: 15, lineHeight: 22 },
});

function NarrationBeat({ beat, dark }: { beat: SceneBeat; dark: boolean }) {
  return (
    <View style={{ marginVertical: 12, paddingHorizontal: 16, alignItems: 'center' }}>
      <Text style={{ fontSize: 14, fontStyle: 'italic', lineHeight: 22, textAlign: 'center', color: dark ? '#9CA3AF' : '#6B7280' }}>
        {beat.text}
      </Text>
    </View>
  );
}
function renderBeat(beat: SceneBeat, dark: boolean, large?: boolean) {
  if (beat.type === 'narration') return <NarrationBeat beat={beat} dark={dark} />;
  if (beat.type === 'action') return <ActionBeat beat={beat} dark={dark} />;
  return <DialogueBeat beat={beat} dark={dark} large={large} />;
}

function renderBeatAnimated(beat: SceneBeat, dark: boolean, onComplete: () => void, skipRef: React.MutableRefObject<boolean>) {
  if (beat.type === 'narration') return <NarrationBeatAnimated beat={beat} dark={dark} onComplete={onComplete} skipRef={skipRef} />;
  if (beat.type === 'action') return <ActionBeatAnimated beat={beat} dark={dark} onComplete={onComplete} skipRef={skipRef} />;
  return <DialogueBeatAnimated beat={beat} dark={dark} onComplete={onComplete} skipRef={skipRef} />;
}

// ─── Static replay mode ───────────────────────────────────────────────────────

function StaticScene({
  sceneData,
  sceneNumber,
  title,
  colors,
  isDarkTheme,
  navigation,
  onNext,
  isLastItem,
}: {
  sceneData: ReturnType<typeof getSceneData>;
  sceneNumber: number;
  title: string;
  colors: ReturnType<typeof getThemeColors>;
  isDarkTheme: boolean;
  navigation: any;
  onNext: () => void;
  isLastItem: boolean;
}) {
  const beats = sceneData?.beats ?? [];
  return (
    <SafeAreaView style={[ss.container, { backgroundColor: colors.background }]}>
      <View style={[ss.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={ss.back}>
          <Ionicons name="arrow-back" size={22} color={colors.iconPrimary} />
        </TouchableOpacity>
        <View style={ss.headerCenter}>
          <View style={[ss.badge, { backgroundColor: '#10B981' + '22' }]}>
            <Text style={[ss.badgeText, { color: '#10B981' }]}>Scene {sceneNumber}</Text>
          </View>
          <Text style={[ss.headerTitle, { color: colors.textPrimary }]} numberOfLines={1}>{title}</Text>
        </View>
        <View style={{ width: 40 }} />
      </View>
      <ScrollView contentContainerStyle={ss.content}>
        {sceneData?.location ? (
          <View style={[ss.locationCard, { backgroundColor: isDarkTheme ? '#1F2937' : '#F3F4F6', borderColor: isDarkTheme ? '#374151' : '#E5E7EB' }]}>
            <Ionicons name="location" size={14} color={isDarkTheme ? '#9CA3AF' : '#6B7280'} />
            <Text style={[ss.locationText, { color: isDarkTheme ? '#9CA3AF' : '#6B7280' }]}>{sceneData.location}</Text>
          </View>
        ) : null}
        {beats.map((beat, i) => (
          <View key={i}>{renderBeat(beat, isDarkTheme, false)}</View>
        ))}
        <View style={ss.actionRow}>
          <TouchableOpacity style={[ss.closePill, { borderColor: colors.border }]} onPress={() => navigation.goBack()}>
            <Text style={[ss.closePillText, { color: colors.textSecondary }]}>{'✕'} Fermer</Text>
          </TouchableOpacity>
          {!isLastItem && (
            <TouchableOpacity style={[ss.nextPill, { backgroundColor: colors.primary }]} onPress={onNext}>
              <Text style={ss.nextPillText}>Suivant →</Text>
            </TouchableOpacity>
          )}
          {isLastItem && (
            <TouchableOpacity style={[ss.nextPill, { backgroundColor: colors.success || '#10B981' }]} onPress={onNext}>
              <Text style={ss.nextPillText}>Terminer</Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
const ss = StyleSheet.create({
  container: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, gap: 10 },
  back: { padding: 8 },
  headerCenter: { flex: 1, alignItems: 'center', gap: 4 },
  badge: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 10 },
  badgeText: { fontSize: 11, fontWeight: '700' },
  headerTitle: { fontSize: 14, fontWeight: '600' },
  content: { padding: 16, paddingBottom: 32 },
  locationCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, borderWidth: 1, borderRadius: 8, padding: 12, marginBottom: 16 },
  locationText: { fontSize: 13, fontStyle: 'italic', flex: 1, lineHeight: 18 },
  actionRow: { flexDirection: 'row', justifyContent: 'flex-end', paddingTop: 20, gap: 8 },
  closePill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1, backgroundColor: 'transparent' },
  closePillText: { fontSize: 14, fontWeight: '600' },
  nextPill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 },
  nextPillText: { color: '#FFF', fontSize: 14, fontWeight: '600' },
});

// ─── Animated cinematic mode ──────────────────────────────────────────────────

export default function SceneReaderScreen({ navigation: navProp, route: routeProp }: { navigation?: any; route?: any } = {}) {
  const navigation = navProp ?? useNavigation<any>();
  const route = routeProp ?? useRoute<any>();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { playerName, playerAvatar } = usePlayer();
  // Sync module-level vars so all beat renderers pick up current player info
  _playerName = playerName || 'Joueur';
  _playerAvatar = playerAvatar ?? null;

  const { sceneNumber, missionId, title } = route.params ?? {};
  const _storyKeyMap: Record<number, string> = {
    1: 'egocrisis', 2: 'foundation_gamble', 3: 'data_storm',
    102: 'architecture_under_fire', 201: 'architecture_under_fire',
    109: 'cloud_heist', 110: 'neural_override', 111: 'pipeline_inferno',
    104: 'quantum_gambit', 105: 'learning_machine', 106: 'ink_protocol',
    108: 'arabic_modern', 112: 'arabic_modern', 113: 'arabic_modern',
    114: 'arabic_modern', 115: 'arabic_modern',
  };
  const storyKey = _storyKeyMap[missionId] ?? 'egocrisis';
  _currentStoryKey = storyKey;
  _missionId = missionId ?? 0;
  const sceneKey = `${missionId ?? 0}-${sceneNumber ?? 0}`;
  const scenarioSceneData = getScenarioSceneData(missionId, route.params?.chapterId, sceneNumber);
  const sceneData = scenarioSceneData ?? getSceneData(sceneNumber ?? 1);
  const beats = sceneData?.beats ?? [];

  const alreadySeen = seenScenes.has(sceneKey);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [isComplete, setIsComplete] = useState(alreadySeen);
  const [showStatic, setShowStatic] = useState(alreadySeen);

  const fadeAnim = useRef(new Animated.Value(alreadySeen ? 1 : 0)).current;
  const [isTextComplete, setIsTextComplete] = useState(alreadySeen);
  const skipRef = useRef<boolean>(false);

  const animateIn = useCallback(() => {
    fadeAnim.setValue(0);
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 350,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [fadeAnim]);

  useEffect(() => {
    if (!alreadySeen) animateIn();
  }, [currentIdx, alreadySeen, animateIn]);

  useEffect(() => {
    if (!alreadySeen) {
      skipRef.current = false;
      setIsTextComplete(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx]);

  const handleNext = () => {
    if (currentIdx < beats.length - 1) {
      skipRef.current = false;
      setIsTextComplete(false);
      setCurrentIdx(prev => prev + 1);
    } else {
      seenScenes.add(sceneKey);
      setIsComplete(true);
      setShowStatic(true);
    }
  };

  const handleNextItem = () => {
    const nextId = route.params?.nextItemId;
    const nextType = route.params?.nextItemType;
    const nextTitle = route.params?.nextItemTitle;
    const nextSceneNumber = route.params?.nextItemSceneNumber;
    const isLast = route.params?.isLastItem;
    if (isLast || !nextType) {
      navigation.goBack();
      return;
    }
    const params = { missionId, chapterId: route.params?.chapterId, itemId: nextId, title: nextTitle };
    switch (nextType) {
      case 'scene':
        navigation.replace('SceneReader' as never, { ...params, sceneNumber: nextSceneNumber, nextItemId: null } as never);
        break;
      case 'chat':
        navigation.navigate('MissionChat' as never, params as never);
        break;
      case 'card':
        navigation.navigate('MissionCards' as never, params as never);
        break;
      case 'quiz':
        navigation.navigate('Quiz' as never, params as never);
        break;
      case 'task':
        navigation.navigate('TaskDetail' as never, params as never);
        break;
    }
  };

  if (showStatic) {
    return (
      <StaticScene
        sceneData={sceneData}
        sceneNumber={sceneNumber}
        title={title ?? sceneData?.title ?? ''}
        colors={colors}
        isDarkTheme={isDarkTheme}
        navigation={navigation}
        onNext={handleNextItem}
        isLastItem={route.params?.isLastItem ?? false}
      />
    );
  }

  const currentBeat = beats[currentIdx];
  const BG = isDarkTheme ? '#0D1117' : '#111827';

  return (
    <View style={[cs.container, { backgroundColor: BG }]}>
      {/* Header */}
      <SafeAreaView>
        <View style={cs.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={cs.backBtn}>
            <Ionicons name="arrow-back" size={22} color="#FFF" />
          </TouchableOpacity>
          <View style={[cs.badge, { backgroundColor: '#10B98133' }]}>
            <Text style={cs.badgeText}>Scene {sceneNumber} — {sceneData?.title ?? title}</Text>
          </View>
          <Text style={cs.progress}>{currentIdx + 1}/{beats.length}</Text>
        </View>
      </SafeAreaView>

      {/* Location card — first beat only */}
      {currentIdx === 0 && sceneData?.location ? (
        <View style={cs.locationCard}>
          <Text style={cs.locationText}>{sceneData.location}</Text>
        </View>
      ) : null}

      {/* Beat area — tappable */}
      <TouchableOpacity
        style={cs.beatArea}
        onPress={() => {
          if (!isTextComplete) {
            skipRef.current = true;
          } else {
            handleNext();
          }
        }}
        activeOpacity={1}
      >
        <Animated.View style={[cs.beatContent, { opacity: fadeAnim }]}>
          {currentBeat ? renderBeatAnimated(currentBeat, isDarkTheme, () => setIsTextComplete(true), skipRef) : null}
        </Animated.View>
      </TouchableOpacity>

      {/* Bottom bar */}
      <SafeAreaView>
        <View style={cs.bottomBar}>
          {/* Progress dots */}
          <View style={cs.dots}>
            {beats.map((_, i) => (
              <View
                key={i}
                style={[
                  cs.dot,
                  {
                    backgroundColor: i < currentIdx ? '#10B981' : i === currentIdx ? '#FFF' : 'rgba(255,255,255,0.25)',
                    width: i === currentIdx ? 16 : 6,
                  },
                ]}
              />
            ))}
          </View>

          {isTextComplete && (
            currentIdx < beats.length - 1 ? (
              <TouchableOpacity style={cs.nextBtn} onPress={handleNext}>
                <Text style={cs.nextBtnText}>Suivant</Text>
                <Ionicons name="arrow-forward" size={16} color="#FFF" />
              </TouchableOpacity>
            ) : (
              <TouchableOpacity style={[cs.nextBtn, { backgroundColor: '#10B981' }]} onPress={handleNext}>
                <Text style={cs.nextBtnText}>Terminer la scene</Text>
                <Ionicons name="checkmark-circle" size={16} color="#FFF" />
              </TouchableOpacity>
            )
          )}
        </View>
      </SafeAreaView>
    </View>
  );
}

const cs = StyleSheet.create({
  container: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 10 },
  backBtn: { padding: 6 },
  badge: { flex: 1, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10, alignItems: 'center' },
  badgeText: { color: '#10B981', fontSize: 12, fontWeight: '700' },
  progress: { color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: '600', minWidth: 30, textAlign: 'right' },
  locationCard: {
    marginHorizontal: 16,
    marginBottom: 8,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderLeftWidth: 3,
    borderLeftColor: '#6B7280',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
  },
  locationText: { color: '#9CA3AF', fontSize: 13, fontStyle: 'italic', lineHeight: 18 },
  beatArea: { flex: 1, justifyContent: 'center', paddingHorizontal: 16 },
  beatContent: { width: '100%' },
  bottomBar: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 12 },
  dots: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 4 },
  dot: { height: 6, borderRadius: 3 },
  nextBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(255,255,255,0.12)',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
  },
  nextBtnText: { color: '#FFF', fontSize: 14, fontWeight: '600' },
});










